import { ManagementMember, ManagementTypes } from './types';

export interface CreateTaskData {
  bidding_id: number;
  client_id: number;
  created_by_id: number;
  responsible_id: number | null;
  title: string;
  description: string;
  deadline: string;
  status_task_id: string;
  finished_date: string | null;
}

export interface UpdateTaskData {
  id: number;
  title: string;
  description: string;
  deadline: string;
  finished_date: string | null;
  responsible_id: number | null;
  status_task_id: string;
  companyId: number;
  scope?: string | null;
}
export interface CreateProgressData {
  bidding_id: number;
  title: string;
  description: string;
  deadline: string;
  companyId: number;
}

export interface UpdateProgressData {
  id: number;
  title: string;
  description: string;
  deadline: string;
  companyId: number;
}

export const getManagementSummary = (scope: string, companyId: number) => ({
  types: [
    ManagementTypes.GET_SUMMARY_REQUEST,
    ManagementTypes.GET_SUMMARY_SUCCESS,
    ManagementTypes.GET_SUMMARY_FAILURE,
  ],
  promise: (client: any) =>
    client.get('/gerenciar_licitacoes/resumo.json', { params: { scope, companyId } }),
});

export const getBiddingSummary = (biddingId: number, companyId: number) => ({
  types: [
    ManagementTypes.GET_BIDDING_SUMMARY_REQUEST,
    ManagementTypes.GET_BIDDING_SUMMARY_SUCCESS,
    ManagementTypes.GET_BIDDING_SUMMARY_FAILURE,
  ],
  promise: (client: any) =>
    client.get(`/gerenciar_licitacoes/resumo/${biddingId}`, { params: { companyId } }),
  biddingId,
});

export const getCalendarEvents = (
  scope: string,
  companyId: number,
  start: string,
  end: string,
  types?: Array<'biddings' | 'tasks' | 'progress'>,
  cancelToken?: any,
) => ({
  types: [
    ManagementTypes.GET_CALENDAR_EVENTS_REQUEST,
    ManagementTypes.GET_CALENDAR_EVENTS_SUCCESS,
    ManagementTypes.GET_CALENDAR_EVENTS_FAILURE,
  ],
  promise: (client: any) =>
    client.get('/gerenciar_licitacoes/calendario.json', {
      params: {
        scope,
        companyId,
        start,
        end,
        types: types ?? ['biddings', 'tasks', 'progress'],
      },
      cancelToken,
    }),
});

export interface PaginatedQuery {
  page?: number;
  per_page?: number;
  q?: string;
  date_filter?: Array<string>;
  deadline_filter?: Array<string>;
  status_in?: Array<string>;
  read?: boolean | null | '__none__';
}

export const EMPTY_FILTER_TOKEN = '__none__';

const withEmptyFilterMarker = (values?: Array<string>): Array<string> | undefined => {
  if (values === undefined) return undefined;
  return values.length ? values : [EMPTY_FILTER_TOKEN];
};

export const getManaged = (scope: string, companyId: number, query: PaginatedQuery = {}) => {
  const params = {
    scope,
    companyId,
    page: query.page,
    per_page: query.per_page,
    q: query.q,
    date_filter: withEmptyFilterMarker(query.date_filter),
  };
  return {
    types: [
      ManagementTypes.GET_MANAGED_REQUEST,
      ManagementTypes.GET_MANAGED_SUCCESS,
      ManagementTypes.GET_MANAGED_FAILURE,
    ],
    promise: (client: any) => client.get('/gerenciar_licitacoes/gerenciar.json', { params }),
  };
};

export const getTasks = (
  scope: string,
  companyId: number,
  biddingId?: number | null,
  query: PaginatedQuery = {},
) => {
  const params = {
    scope,
    companyId,
    bidding_id: biddingId || null,
    page: query.page,
    per_page: query.per_page,
    q: query.q,
    deadline_filter: withEmptyFilterMarker(query.deadline_filter),
  };
  return {
    types: [
      ManagementTypes.GET_TASKS_REQUEST,
      ManagementTypes.GET_TASKS_SUCCESS,
      ManagementTypes.GET_TASKS_FAILURE,
    ],
    biddingId,
    promise: (client: any) => client.get('/gerenciar_licitacoes/tarefas.json', { params }),
  };
};

export const getBiddingTimeline = (companyId: number, biddingId: number) => ({
  types: [
    ManagementTypes.GET_BIDDING_TIMELINE_REQUEST,
    ManagementTypes.GET_BIDDING_TIMELINE_SUCCESS,
    ManagementTypes.GET_BIDDING_TIMELINE_FAILURE,
  ],
  promise: (client: any) =>
    client.get(`/gerenciar_licitacoes/linha_do_tempo/${biddingId}`, { params: { companyId } }),
});

export const getTaskById = (taskId: number, companyId: number) => ({
  types: [
    ManagementTypes.GET_TASK_BY_ID_REQUEST,
    ManagementTypes.GET_TASK_BY_ID_SUCCESS,
    ManagementTypes.GET_TASK_BY_ID_FAILURE,
  ],
  taskId,
  promise: (client: any) =>
    client.get(`/gerenciar_licitacoes/tarefas/${taskId}`, { params: { companyId } }),
});

export const clearModalTask = () => ({
  type: ManagementTypes.CLEAR_MODAL_TASK,
});

export const createManaged = (biddingId: number, companyId: number) => {
  const data = {
    bidding_id: biddingId,
    companyId,
  };
  return {
    types: [
      ManagementTypes.CREATE_MANAGED_REQUEST,
      ManagementTypes.CREATE_MANAGED_SUCCESS,
      ManagementTypes.CREATE_MANAGED_FAILURE,
    ],
    biddingId,
    companyId,
    promise: (client: any) => client.post('/gerenciar_licitacoes/gerenciar', data),
  };
};

export const destroyManaged = (biddingId: number, companyId: number, contact_client_id: number) => {
  const params = {
    data: {
      bidding_id: biddingId,
      companyId,
      contact_client_id,
    },
  };
  return {
    types: [
      ManagementTypes.DELETE_MANAGED_REQUEST,
      ManagementTypes.DELETE_MANAGED_SUCCESS,
      ManagementTypes.DELETE_MANAGED_FAILURE,
    ],
    biddingId,
    contact_client_id,
    promise: (client: any) => client.delete(`/gerenciar_licitacoes/gerenciar/${biddingId}`, params),
  };
};

export interface BatchChoice {
  memberIds?: Array<number>;
}

const batchChoiceParams = ({ memberIds }: BatchChoice) => ({
  ...(memberIds ? { member_ids: memberIds } : {}),
});

export const getManagementMembers = (companyId: number) => ({
  types: [
    ManagementTypes.GET_MANAGEMENT_MEMBERS_REQUEST,
    ManagementTypes.GET_MANAGEMENT_MEMBERS_SUCCESS,
    ManagementTypes.GET_MANAGEMENT_MEMBERS_FAILURE,
  ],
  companyId,
  promise: (client: any) =>
    client.get('/gerenciar_licitacoes/membros.json', { params: { companyId } }),
});

export const destroyManagedBatch = (
  biddingIds: Array<number>,
  companyId: number,
  scope: string | null = null,
  choice: BatchChoice = {},
) => {
  const params = {
    data: {
      bidding_ids: biddingIds,
      companyId,
      scope,
      ...batchChoiceParams(choice),
    },
  };
  return {
    types: [
      ManagementTypes.DELETE_MANAGED_BATCH_REQUEST,
      ManagementTypes.DELETE_MANAGED_BATCH_SUCCESS,
      ManagementTypes.DELETE_MANAGED_BATCH_FAILURE,
    ],
    biddingIds,
    scope,
    promise: (client: any) =>
      client.delete('/gerenciar_licitacoes/gerenciar/batch_destroy', params),
  };
};

export const reactivateManagedBatch = (
  biddingIds: Array<number>,
  companyId: number,
  scope: string | null = null,
  choice: BatchChoice = {},
) => {
  const params = {
    bidding_ids: biddingIds,
    companyId,
    scope,
    ...batchChoiceParams(choice),
  };
  return {
    types: [
      ManagementTypes.REACTIVATE_MANAGED_BATCH_REQUEST,
      ManagementTypes.REACTIVATE_MANAGED_BATCH_SUCCESS,
      ManagementTypes.REACTIVATE_MANAGED_BATCH_FAILURE,
    ],
    biddingIds,
    scope,
    promise: (client: any) =>
      client.patch('/gerenciar_licitacoes/gerenciar/batch_reactivate', params),
  };
};

export interface BatchFinishBiddingData {
  id: number;
  amount?: string;
}

export const finishManagedBatch = (
  biddings: Array<BatchFinishBiddingData>,
  finished_management_status: string,
  comments: string,
  companyId: number,
  scope: string | null = null,
  choice: BatchChoice = {},
) => {
  const bidding_finishes = biddings.map((b) => ({ bidding_id: b.id, amount: b.amount }));
  const params = {
    bidding_finishes,
    finished_management_status,
    comments,
    companyId,
    scope,
    ...batchChoiceParams(choice),
  };
  return {
    types: [
      ManagementTypes.FINISH_MANAGED_BATCH_REQUEST,
      ManagementTypes.FINISH_MANAGED_BATCH_SUCCESS,
      ManagementTypes.FINISH_MANAGED_BATCH_FAILURE,
    ],
    biddings,
    finishedManagementStatus: finished_management_status,
    comments,
    scope,
    promise: (client: any) => client.patch('/gerenciar_licitacoes/gerenciar/batch_finish', params),
  };
};

export type TaskStatusChange = 'finished' | 'in_progress' | null;

export const updateTasksStatusBatch = (
  taskIds: Array<number>,
  options: {
    statusChange?: TaskStatusChange;
    deadline?: string | null;
    responsible?: ManagementMember | null;
  },
  companyId: number,
  scope: string | null = null,
  choice: BatchChoice = {},
) => {
  const params = {
    task_ids: taskIds,
    status_change: options.statusChange || null,
    deadline: options.deadline || null,
    ...(options.responsible ? { responsible_id: options.responsible.id } : {}),
    companyId,
    scope,
    ...batchChoiceParams(choice),
  };
  return {
    types: [
      ManagementTypes.UPDATE_TASKS_STATUS_BATCH_REQUEST,
      ManagementTypes.UPDATE_TASKS_STATUS_BATCH_SUCCESS,
      ManagementTypes.UPDATE_TASKS_STATUS_BATCH_FAILURE,
    ],
    taskIds,
    statusChange: options.statusChange || null,
    deadline: options.deadline || null,
    responsible: options.responsible || null,
    scope,
    promise: (client: any) =>
      client.patch('/gerenciar_licitacoes/tarefas/batch_update_status', params),
  };
};

export const markReadProgressBatch = (notificationIds: Array<number>) => {
  const params = {
    notification_progress_ids: notificationIds,
  };
  return {
    types: [
      ManagementTypes.MARK_READ_PROGRESS_BATCH_REQUEST,
      ManagementTypes.MARK_READ_PROGRESS_BATCH_SUCCESS,
      ManagementTypes.MARK_READ_PROGRESS_BATCH_FAILURE,
    ],
    notificationIds,
    promise: (client: any) => client.patch('/notification/new_progress/batch_mark_read', params),
  };
};

export const markUnreadProgressBatch = (notificationIds: Array<number>) => {
  const params = {
    notification_progress_ids: notificationIds,
  };
  return {
    types: [
      ManagementTypes.MARK_UNREAD_PROGRESS_BATCH_REQUEST,
      ManagementTypes.MARK_UNREAD_PROGRESS_BATCH_SUCCESS,
      ManagementTypes.MARK_UNREAD_PROGRESS_BATCH_FAILURE,
    ],
    notificationIds,
    promise: (client: any) => client.patch('/notification/new_progress/batch_mark_unread', params),
  };
};

export const deleteTasksBatch = (
  taskIds: Array<number>,
  companyId: number,
  scope: string | null = null,
  choice: BatchChoice = {},
) => {
  const params = {
    data: {
      task_ids: taskIds,
      companyId,
      scope,
      ...batchChoiceParams(choice),
    },
  };
  return {
    types: [
      ManagementTypes.DELETE_TASKS_BATCH_REQUEST,
      ManagementTypes.DELETE_TASKS_BATCH_SUCCESS,
      ManagementTypes.DELETE_TASKS_BATCH_FAILURE,
    ],
    taskIds,
    scope,
    promise: (client: any) => client.delete('/gerenciar_licitacoes/tarefas/batch_destroy', params),
  };
};

export const deleteProgressBatch = (notificationIds: Array<number>) => {
  const params = {
    data: {
      notification_progress_ids: notificationIds,
    },
  };
  return {
    types: [
      ManagementTypes.DELETE_PROGRESS_BATCH_REQUEST,
      ManagementTypes.DELETE_PROGRESS_BATCH_SUCCESS,
      ManagementTypes.DELETE_PROGRESS_BATCH_FAILURE,
    ],
    notificationIds,
    promise: (client: any) =>
      client.delete('/gerenciar_licitacoes/andamentos/batch_destroy', params),
  };
};

export const createTask = (taskData: CreateTaskData) => {
  const params = {
    bidding_id: taskData.bidding_id,
    client_id: taskData.client_id,
    created_by_id: taskData.created_by_id,
    deadline: taskData.deadline,
    description: taskData.description,
    finished_date: taskData.finished_date,
    responsible_id: taskData.responsible_id,
    status_task_id: taskData.status_task_id,
    title: taskData.title,
  };

  return {
    types: [
      ManagementTypes.CREATE_TASKS_REQUEST,
      ManagementTypes.CREATE_TASKS_SUCCESS,
      ManagementTypes.CREATE_TASKS_FAILURE,
    ],
    promise: (client: any) => client.post('/gerenciar_licitacoes/tarefas.json', params),
  };
};

export const updateTask = (taskData: UpdateTaskData) => {
  const params = {
    title: taskData.title,
    description: taskData.description,
    deadline: taskData.deadline,
    finished_date: taskData.finished_date,
    responsible_id: taskData.responsible_id,
    status_task_id: taskData.status_task_id,
    companyId: taskData.companyId,
    scope: taskData.scope ?? null,
  };

  return {
    types: [
      ManagementTypes.UPDATE_TASKS_REQUEST,
      ManagementTypes.UPDATE_TASKS_SUCCESS,
      ManagementTypes.UPDATE_TASKS_FAILURE,
    ],
    promise: (client: any) => client.patch(`/gerenciar_licitacoes/tarefas/${taskData.id}`, params),
  };
};

export const deleteTask = (taskId: number, companyId: number) => {
  return {
    types: [
      ManagementTypes.DELETE_TASK_REQUEST,
      ManagementTypes.DELETE_TASK_SUCCESS,
      ManagementTypes.DELETE_TASK_FAILURE,
    ],
    promise: (client: any) =>
      client.delete(`/gerenciar_licitacoes/tarefas/${taskId}`, { data: { companyId } }),
    taskId,
  };
};

export const deleteTaskComment = (commentId: number, taskId: number) => {
  return {
    types: [
      ManagementTypes.DELETE_TASK_COMMENT_REQUEST,
      ManagementTypes.DELETE_TASK_COMMENT_SUCCESS,
      ManagementTypes.DELETE_TASK_COMMENT_FAILURE,
    ],
    promise: (client: any) =>
      client.delete(`/gerenciar_licitacoes/comentarios_tarefa/${commentId}`),
    taskCommentId: commentId,
    taskId,
  };
};

export const createTaskComment = (taskId: number, description: string) => {
  const params = {
    task_id: taskId,
    description,
  };
  return {
    types: [
      ManagementTypes.CREATE_TASK_COMMENT_REQUEST,
      ManagementTypes.CREATE_TASK_COMMENT_SUCCESS,
      ManagementTypes.CREATE_TASK_COMMENT_FAILURE,
    ],
    promise: (client: any) => client.post('/gerenciar_licitacoes/comentarios_tarefa.json', params),
    taskId,
  };
};

export const createProgressComment = (progressId: number, description: string) => {
  const params = {
    task_id: progressId,
    description,
  };
  return {
    types: [
      ManagementTypes.CREATE_PROGRESS_COMMENT_REQUEST,
      ManagementTypes.CREATE_PROGRESS_COMMENT_SUCCESS,
      ManagementTypes.CREATE_PROGRESS_COMMENT_FAILURE,
    ],
    promise: (client: any) => client.post('/gerenciar_licitacoes/comentarios_tarefa.json', params),
    progressId,
  };
};

export const createTaskAttachment = (
  formData: FormData,
  taskId: number,
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>,
) => {
  return {
    types: [
      ManagementTypes.CREATE_TASK_ATTACHMENT_REQUEST,
      ManagementTypes.CREATE_TASK_ATTACHMENT_SUCCESS,
      ManagementTypes.CREATE_TASK_ATTACHMENT_FAILURE,
    ],
    promise: (client: any) =>
      client.post('/gerenciar_licitacoes/anexos', formData, {
        onUploadProgress: (event: any) => {
          const progress: number = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(progress);
        },
      }),
    taskId,
  };
};

export const createProgressAttachment = (
  formData: FormData,
  progressId: number,
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>,
) => {
  return {
    types: [
      ManagementTypes.CREATE_PROGRESS_ATTACHMENT_REQUEST,
      ManagementTypes.CREATE_PROGRESS_ATTACHMENT_SUCCESS,
      ManagementTypes.CREATE_PROGRESS_ATTACHMENT_FAILURE,
    ],
    promise: (client: any) =>
      client.post('/gerenciar_licitacoes/anexos', formData, {
        onUploadProgress: (event: any) => {
          const progress: number = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(progress);
        },
      }),
    progressId,
  };
};

export const destroyTaskAttachment = (biddingAttachmentId: number, taskId: number) => ({
  types: [
    ManagementTypes.DELETE_TASK_ATTACHMENT_REQUEST,
    ManagementTypes.DELETE_TASK_ATTACHMENT_SUCCESS,
    ManagementTypes.DELETE_TASK_ATTACHMENT_FAILURE,
  ],
  promise: (client: any) => client.delete(`/gerenciar_licitacoes/anexos/${biddingAttachmentId}`),
  biddingAttachmentId,
  taskId,
});

export const getNotificationProgress = (
  companyId: number,
  biddingId?: number | null,
  query: PaginatedQuery = {},
) => {
  const params = {
    companyId,
    bidding_id: biddingId || null,
    page: query.page,
    per_page: query.per_page,
    read: query.read,
  };
  return {
    types: [
      ManagementTypes.GET_NOTIFICATION_PROGRESS_REQUEST,
      ManagementTypes.GET_NOTIFICATION_PROGRESS_SUCCESS,
      ManagementTypes.GET_NOTIFICATION_PROGRESS_FAILURE,
    ],
    biddingId,
    promise: (client: any) => client.get('/gerenciar_licitacoes/andamentos.json', { params }),
  };
};

export const createProgress = (progressData: CreateProgressData) => {
  const params = {
    bidding_id: progressData.bidding_id,
    deadline: progressData.deadline,
    description: progressData.description,
    title: progressData.title,
    companyId: progressData.companyId,
  };
  return {
    types: [
      ManagementTypes.CREATE_PROGRESS_REQUEST,
      ManagementTypes.CREATE_PROGRESS_SUCCESS,
      ManagementTypes.CREATE_PROGRESS_FAILURE,
    ],
    promise: (client: any) => client.post('/gerenciar_licitacoes/andamentos.json', params),
  };
};

export const updateProgress = (progressData: UpdateProgressData) => {
  const params = {
    title: progressData.title,
    description: progressData.description,
    deadline: progressData.deadline,
    companyId: progressData.companyId,
  };
  return {
    types: [
      ManagementTypes.UPDATE_PROGRESS_REQUEST,
      ManagementTypes.UPDATE_PROGRESS_SUCCESS,
      ManagementTypes.UPDATE_PROGRESS_FAILURE,
    ],
    promise: (client: any) =>
      client.patch(`/gerenciar_licitacoes/andamentos/${progressData.id}`, params),
  };
};

export const deleteProgress = (progressId: number, companyId: number) => {
  return {
    types: [
      ManagementTypes.DELETE_PROGRESS_REQUEST,
      ManagementTypes.DELETE_PROGRESS_SUCCESS,
      ManagementTypes.DELETE_PROGRESS_FAILURE,
    ],
    promise: (client: any) =>
      client.delete(`/gerenciar_licitacoes/andamentos/${progressId}`, { data: { companyId } }),
    progressId,
  };
};

export const updateNotificationProgress = (notificationProgressId: number) => {
  return {
    types: [
      ManagementTypes.UPDATE_NOTIFICATION_PROGRESS_REQUEST,
      ManagementTypes.UPDATE_NOTIFICATION_PROGRESS_SUCCESS,
      ManagementTypes.UPDATE_NOTIFICATION_PROGRESS_FAILURE,
    ],
    promise: (client: any) => client.patch(`/notification/new_progress/${notificationProgressId}`),
    notificationProgressId,
  };
};

export const getFinished = (scope: string, companyId: number, query: PaginatedQuery = {}) => ({
  types: [
    ManagementTypes.GET_FINISHED_REQUEST,
    ManagementTypes.GET_FINISHED_SUCCESS,
    ManagementTypes.GET_FINISHED_FAILURE,
  ],
  promise: (client: any) =>
    client.get('/gerenciar_licitacoes/finalizadas.json', {
      params: {
        scope,
        companyId,
        page: query.page,
        per_page: query.per_page,
        q: query.q,
        status_in: withEmptyFilterMarker(query.status_in),
      },
    }),
});

export const finishManaged = (
  biddingId: number,
  finished_management_status: string,
  comments: string,
  companyId: number,
  amount?: string | undefined,
) => {
  const params = {
    archived: true,
    finished_management_status,
    comments,
    companyId,
    amount,
  };
  return {
    types: [
      ManagementTypes.FINISH_MANAGED_REQUEST,
      ManagementTypes.FINISH_MANAGED_SUCCESS,
      ManagementTypes.FINISH_MANAGED_FAILURE,
    ],
    promise: (client: any) => client.patch(`/gerenciar_licitacoes/gerenciar/${biddingId}`, params),
    biddingId,
    finishedManagementStatus: finished_management_status,
    amount,
    comments,
  };
};

export const reactivateManaged = (biddingId: number) => {
  const params = {
    reactivate: true,
  };
  return {
    types: [
      ManagementTypes.REACTIVATE_MANAGED_REQUEST,
      ManagementTypes.REACTIVATE_MANAGED_SUCCESS,
      ManagementTypes.REACTIVATE_MANAGED_FAILURE,
    ],
    promise: (client: any) => client.patch(`/gerenciar_licitacoes/gerenciar/${biddingId}`, params),
    biddingId,
  };
};

export const getBiddingManagementAttachments = (biddingId: number, companyId: number) => {
  return {
    types: [
      ManagementTypes.GET_MANAGEMENT_ATTACHMENTS_REQUEST,
      ManagementTypes.GET_MANAGEMENT_ATTACHMENTS_SUCCESS,
      ManagementTypes.GET_MANAGEMENT_ATTACHMENTS_FAILURE,
    ],
    promise: (client: any) =>
      client.get('/gerenciar_licitacoes/anexos', { params: { biddingId, companyId } }),
    biddingId,
  };
};

export const createBiddingManagementAttachment = (
  formData: FormData,
  fileName: string,
  biddingId: string,
  updateUploadProgress: (fileName: string, progress: number) => void,
) => {
  return {
    types: [
      ManagementTypes.CREATE_MANAGEMENT_ATTACHMENTS_REQUEST,
      ManagementTypes.CREATE_MANAGEMENT_ATTACHMENTS_SUCCESS,
      ManagementTypes.CREATE_MANAGEMENT_ATTACHMENTS_FAILURE,
    ],
    promise: (client: any) =>
      client.post('/gerenciar_licitacoes/anexos', formData, {
        onUploadProgress: (event: any) => {
          const progress: number = Math.round((event.loaded * 100) / event.total);
          updateUploadProgress(fileName, progress);
        },
      }),
    fileName,
    biddingId,
  };
};

export const destroyBiddingAttachment = (biddingAttachmentId: number, biddingId: number) => ({
  types: [
    ManagementTypes.DELETE_MANAGEMENT_ATTACHMENTS_REQUEST,
    ManagementTypes.DELETE_MANAGEMENT_ATTACHMENTS_SUCCESS,
    ManagementTypes.DELETE_MANAGEMENT_ATTACHMENTS_FAILURE,
  ],
  promise: (client: any) => client.delete(`/gerenciar_licitacoes/anexos/${biddingAttachmentId}`),
  biddingAttachmentId,
  biddingId,
});

export const getManagementPreferences = (clientId: number) => ({
  types: [
    ManagementTypes.GET_MANAGEMENT_PREFERENCES_REQUEST,
    ManagementTypes.GET_MANAGEMENT_PREFERENCES_SUCCESS,
    ManagementTypes.GET_MANAGEMENT_PREFERENCES_FAILURE,
  ],
  promise: (client: any) =>
    client.get(`/gerenciar_licitacoes/configuracoes`, { params: { client_id: clientId } }),
});

export const updateManagementPreferences = (
  id: number,
  email_preference: boolean,
  whatsapp_preference: boolean,
  push_preference: boolean,
  clarification_preference: boolean,
  notice_preference: boolean,
  impugnment_preference: boolean,
  appeal_preference: boolean,
  status_preference: boolean,
  result_preference: boolean,
) => {
  const params = {
    email_notification: email_preference,
    whatsapp_notification: whatsapp_preference,
    push_notification: push_preference,
    clarification_notification: clarification_preference,
    notice_notification: notice_preference,
    impugnment_notification: impugnment_preference,
    appeal_notification: appeal_preference,
    status_notification: status_preference,
    result_notification: result_preference,
  };
  return {
    types: [
      ManagementTypes.UPDATE_MANAGEMENT_PREFERENCES_REQUEST,
      ManagementTypes.UPDATE_MANAGEMENT_PREFERENCES_SUCCESS,
      ManagementTypes.UPDATE_MANAGEMENT_PREFERENCES_FAILURE,
    ],
    promise: (client: any) => client.patch(`/gerenciar_licitacoes/configuracoes/${id}`, params),
  };
};

export const getBiddingOverview = (bidding_id: string | number) => ({
  types: [
    ManagementTypes.GET_BIDDING_OVERVIEW_REQUEST,
    ManagementTypes.GET_BIDDING_OVERVIEW_SUCCESS,
    ManagementTypes.GET_BIDDING_OVERVIEW_FAILURE,
  ],
  biddingId: bidding_id,
  promise: (client: any) => client.get(`/resumo-licitacao`, { params: { bidding_id } }),
});
